import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import type {
  ModuleConfig,
  ReorderUpdate,
  UpdateModulePayload,
} from "../types";

const KEY = ["modules"] as const;
const NAV_CACHE_KEY = "phazex_nav_modules_cache";

function readNavCache(): ModuleConfig[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(NAV_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed as ModuleConfig[];
  } catch {
    return null;
  }
}

function writeNavCache(modules: ModuleConfig[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(NAV_CACHE_KEY, JSON.stringify(modules));
  } catch {
    // ignore
  }
}

export function clearNavCache(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(NAV_CACHE_KEY);
  } catch {
    // ignore
  }
}

export function useModuleList(): UseQueryResult<ModuleConfig[]> {
  const { accessToken } = useAuth();
  const tokenKey = accessToken ? "auth" : "anon";
  return useQuery({
    queryKey: [...KEY, "list", tokenKey],
    enabled: !!accessToken,
    queryFn: async () => {
      const res = await apiFetch<{ modules: ModuleConfig[] }>("/modules", {
        token: accessToken,
      });
      return res.modules;
    },
  });
}

export function useNavModules(): UseQueryResult<ModuleConfig[]> {
  const { accessToken } = useAuth();
  const tokenKey = accessToken ? "auth" : "anon";
  return useQuery({
    queryKey: [...KEY, "nav", tokenKey],
    enabled: !!accessToken,
    initialData: accessToken
      ? () => readNavCache() ?? undefined
      : undefined,
    initialDataUpdatedAt: 0,
    // Reuse cached nav across rapid page navigations (Sidebar remounts on
    // route change). 30s is short enough that admin module-manager edits show
    // up quickly while still collapsing dozens of in-burst refetches into one.
    staleTime: 30_000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const res = await apiFetch<{ modules: ModuleConfig[] }>("/modules/nav", {
        token: accessToken,
      });
      writeNavCache(res.modules);
      return res.modules;
    },
  });
}

export function useUpdateModule() {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateModulePayload;
    }) => {
      const res = await apiFetch<{ module: ModuleConfig }>(`/modules/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      });
      return res.module;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useReorderModules() {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: async (updates: ReorderUpdate[]) =>
      apiFetch<{ success: true; updated: number }>("/modules/reorder", {
        method: "POST",
        body: { updates },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
